/**
 * Typography bridge between JSON tokens and component code.
 *
 * Exports two things:
 *   1. `text(role)` — a helper used inside styled() and inline styles.
 *      Returns a plain object with fontFamily, fontWeight, fontSize,
 *      lineHeight, letterSpacing. Pass it as a spread:
 *          `...text('label.m')`
 *   2. `muiTypography` — the typography config plugged into createTheme,
 *      mapping AdSensor roles onto MUI's built-in variants so untouched
 *      MUI components also pick up our typography.
 *
 * @format
 */

import typo from 'ui/tokens/semantic/typography.json';

export interface TypographyValue {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight: string;
  letterSpacing: string;
}

const flatten = (t: {$value: TypographyValue}): TypographyValue => t.$value;

export const textTokens = {
  heading: {
    xl: flatten(typo.text.heading.xl),
    l: flatten(typo.text.heading.l),
    m: flatten(typo.text.heading.m),
  },
  body: {
    l: flatten(typo.text.body.l),
    m: flatten(typo.text.body.m),
    s: flatten(typo.text.body.s),
  },
  label: {
    l: flatten(typo.text.label.l),
    m: flatten(typo.text.label.m),
    s: flatten(typo.text.label.s),
  },
  overline: {
    l: flatten(typo.text.overline.l),
    m: flatten(typo.text.overline.m),
    s: flatten(typo.text.overline.s),
    xs: flatten(typo.text.overline.xs),
  },
};

export type TypographyRole =
  | `heading.${keyof typeof textTokens.heading}`
  | `body.${keyof typeof textTokens.body}`
  | `label.${keyof typeof textTokens.label}`
  | `overline.${keyof typeof textTokens.overline}`;

/**
 * Look up a typography role. Returns a plain object suitable for spreading
 * into a CSS-in-JS style object.
 *
 *     const style = { ...text('label.m'), color: '...' };
 */
export function text(role: TypographyRole): TypographyValue {
  const [group, size] = role.split('.') as [keyof typeof textTokens, string];
  const tokens = textTokens[group] as Record<string, TypographyValue>;
  const value = tokens[size];
  if (!value) {
    throw new Error(`Unknown typography role: ${role}`);
  }
  return value;
}

/**
 * System rule: Icons are a typography primitive. The pixel size of an
 * Icon rendered alongside text is the line-height of the text's typography
 * role. This locks the icon's optical mass to the line it sits on — even
 * in icon-only containers (where the icon "stands in for" the would-be
 * text at that size).
 *
 * Use this util everywhere an Icon coexists with a text label OR sits in
 * a container whose size maps to a typography role (Button, Input, Chip,
 * Badge, Snackbar, SegmentedButton, FormField, Link, ...). Components
 * must NOT hard-code icon sizes that compete with the typography role's
 * line-height.
 *
 *     <Icon name="add" size={iconPxForRole('label.m')} />  // 16px
 *
 * For a standalone, decorator-class Icon with no adjacent text, fall back
 * to the explicit Icon.size = 'xs' | 's' | 'm' | 'l' | 'xl' named scale.
 */
export function iconPxForRole(role: TypographyRole): number {
  return parseInt(text(role).lineHeight);
}

/**
 * Icon weight (Material Symbols `wght` axis) paired with the typography
 * role's font-weight. The premise: an Icon that lives next to a label
 * should not visually shout louder than the label. By default Material
 * Symbols renders weight 400; we tie it to the role weight so emphasis
 * lines up.
 *
 * Returns a value snapped to the nearest 100, clamped to the MS axis
 * range (100-700).
 */
export function iconWeightForRole(role: TypographyRole): number {
  const w = text(role).fontWeight;
  const snapped = Math.round(w / 100) * 100;
  return Math.min(700, Math.max(100, snapped));
}

/**
 * MUI typography config. Each MUI variant (h1, body1, etc.) is mapped to
 * an AdSensor role, so that consumers who use raw <Typography variant="h2">
 * still get our system tokens.
 *
 * Mapping rationale:
 *   - h1/h2/h3 → heading.xl/l/m (size-monotonic with MUI convention)
 *   - subtitle1/2 → label.l/s (Medium-weight emphasis)
 *   - body1/body2 → body.l/m
 *   - caption → body.s (smallest readable text)
 *   - button → label.m (default button label)
 *   - overline → overline.s (the most common overline size in design)
 */
export const muiTypography = {
  fontFamily: textTokens.body.m.fontFamily,
  htmlFontSize: 16,
  fontSize: parseInt(textTokens.body.m.fontSize),

  h1: textTokens.heading.xl,
  h2: textTokens.heading.l,
  h3: textTokens.heading.m,
  h4: textTokens.heading.m, // duplicate — we only have 3 heading sizes
  h5: textTokens.label.l, // map h5/h6 onto labels (no real Figma source)
  h6: textTokens.label.m,

  subtitle1: textTokens.label.l,
  subtitle2: textTokens.label.s,

  body1: textTokens.body.l,
  body2: textTokens.body.m,

  caption: textTokens.body.s,
  overline: textTokens.overline.s,
  button: {...textTokens.label.m, textTransform: 'none' as const},
};
